import t from 'react-translate';

export enum ClosingReason {
  COMPLETED = 'complete',
  TERMINATED = 'terminated',
}

type StreamChunk = {
  content: string
  isLastChunk: boolean
  closingReason?: ClosingReason
};

type StreamParams = {
  aiApiEndpoint: string;
  token: string;
  userMessage?: string | null;
  userAudio?: string | null;
  onChunk: (chunk: StreamChunk) => void;
  onError?: (err: any) => void;
  abortSignal?: AbortSignal;
  onAudioDelta?: (data: { audio: string; sequence: number }) => void;
  onAudioDone?: (data: { totalSequences: number }) => void;
};

// SSE event types from AI service
enum SSEEventType {
  Delta = 'delta',
  Done = 'done',
  AudioDelta = 'audio_delta',
  AudioDone = 'audio_done',
  Error = 'error',
}

type DeltaEventData = {
  content: string
};

type DoneEventData = {
  closing_reason?: ClosingReason
};

type AudioDeltaEventData = {
  audio: string;
  sequence: number;
};

type AudioDoneEventData = {
  total_sequences: number;
};

type ErrorEventData = {
  error: string
};

const streamAIResponse = async ({
  aiApiEndpoint,
  token,
  userMessage,
  userAudio,
  onChunk,
  onError,
  abortSignal,
  onAudioDelta,
  onAudioDone,
}: StreamParams) => {
  if (!aiApiEndpoint) {
    const error = new Error(t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.AI_HOST_URL_MISSING_ERROR());
    onError?.(error);
    throw error;
  }

  try {
    const isAudioMode = userAudio !== undefined;

    let body: string;
    if (isAudioMode) {
      body = JSON.stringify({
        user_audio: userAudio,
        ...(userAudio !== null && { user_audio_content_type: 'audio/wav' }),
      });
    } else {
      body = JSON.stringify({ user_message: userMessage });
    }

    const response = await fetch(`${aiApiEndpoint}/role-play/learner/stream`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body,
      signal: abortSignal,
    });

    if (!response.ok || !response.body) {
      throw new Error('Failed to connect to AI stream');
    }

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader();

    let buffer = '';

    const processSSEEvent = (eventType: SSEEventType, data: string): boolean => {
      let shouldCancel = false;

      try {
        switch (eventType) {
          case SSEEventType.Delta: {
            const deltaData: DeltaEventData = JSON.parse(data);
            onChunk({
              content: deltaData.content,
              isLastChunk: false,
            });
            break;
          }
          case SSEEventType.Done: {
            const doneData: DoneEventData = data ? JSON.parse(data) : {};
            onChunk({
              content: '',
              isLastChunk: true,
              closingReason: doneData.closing_reason,
            });
            break;
          }
          case SSEEventType.AudioDelta: {
            const audioDeltaData: AudioDeltaEventData = JSON.parse(data);
            onAudioDelta?.({ audio: audioDeltaData.audio, sequence: audioDeltaData.sequence });
            break;
          }
          case SSEEventType.AudioDone: {
            const audioDoneData: AudioDoneEventData = JSON.parse(data);
            onAudioDone?.({ totalSequences: audioDoneData.total_sequences });
            break;
          }
          case SSEEventType.Error: {
            const errorData: ErrorEventData = JSON.parse(data);
            shouldCancel = true;
            onError?.(new Error(`Error from AI stream: ${errorData.error}`));
            break;
          }
          default:
            // Unknown event type, ignore
            break;
        }
      } catch (err) {
        onError?.(err);
      }

      return shouldCancel;
    };

    const processLines = (lines: string[]): boolean => {
      let shouldCancel = false;
      let currentEventType: SSEEventType | null = null;

      lines.forEach((rawLine) => {
        if (shouldCancel) return;

        const line = rawLine.trim();

        if (line.startsWith('event:')) {
          currentEventType = line.replace(/^event:\s*/, '') as SSEEventType;
        } else if (line.startsWith('data:')) {
          const data = line.replace(/^data:\s*/, '');
          if (currentEventType) {
            shouldCancel = processSSEEvent(currentEventType, data);
            currentEventType = null;
          }
          // Note: data lines without preceding event lines are silently dropped.
          // This is intentional defensive behavior for malformed SSE streams.
        }
      });

      return shouldCancel;
    };

    const readNextChunk = (): Promise<void> => reader.read().then(({ value, done }) => {
      if (done) return Promise.resolve();

      buffer += value;

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      const shouldCancel = processLines(lines);

      if (shouldCancel) {
        return reader.cancel();
      }

      return readNextChunk();
    });

    await readNextChunk();
  } catch (err) {
    // Don't call onError or re-throw for aborted requests - this is intentional cancellation
    if (err instanceof Error && err.name === 'AbortError') {
      return;
    }
    onError?.(err);
    // Re-throw so callers can add their own .catch() for additional cleanup logic
    // e.g., handleStartConversation needs to reset status to NOT_STARTED and clear opening narrative
    throw err;
  }
};

export default streamAIResponse;

type RetryTtsParams = {
  aiApiEndpoint: string | null;
  token: string;
  onAudioDelta?: (data: { audio: string; sequence: number }) => void;
  onAudioDone?: () => void;
  onError?: (err: any) => void;
  abortSignal?: AbortSignal;
};

export const retryTtsStream = async ({
  aiApiEndpoint,
  token,
  onAudioDelta,
  onAudioDone,
  onError,
  abortSignal,
}: RetryTtsParams): Promise<void> => {
  if (!aiApiEndpoint) {
    const error = new Error(t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.AI_HOST_URL_MISSING_ERROR());
    onError?.(error);
    throw error;
  }

  try {
    const response = await fetch(`${aiApiEndpoint}/role-play/learner/retry-tts`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      signal: abortSignal,
    });

    if (!response.ok || !response.body) {
      throw new Error('Failed to connect to retry-tts stream');
    }

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader();

    let buffer = '';

    const processLines = (lines: string[]): boolean => {
      let shouldCancel = false;
      let currentEventType: string | null = null;

      lines.forEach((rawLine) => {
        if (shouldCancel) return;
        const line = rawLine.trim();

        if (line.startsWith('event:')) {
          currentEventType = line.replace(/^event:\s*/, '');
        } else if (line.startsWith('data:')) {
          const data = line.replace(/^data:\s*/, '');
          if (!currentEventType) return;

          try {
            switch (currentEventType) {
              case SSEEventType.AudioDelta: {
                const audioDeltaData: AudioDeltaEventData = JSON.parse(data);
                onAudioDelta?.({ audio: audioDeltaData.audio, sequence: audioDeltaData.sequence });
                break;
              }
              case SSEEventType.AudioDone: {
                onAudioDone?.();
                break;
              }
              case SSEEventType.Error: {
                const errorData: ErrorEventData = JSON.parse(data);
                shouldCancel = true;
                onError?.(new Error(`Error from retry-tts stream: ${errorData.error}`));
                break;
              }
              default:
                break;
            }
          } catch (err) {
            onError?.(err);
          }
          currentEventType = null;
        }
      });

      return shouldCancel;
    };

    const readNextChunk = (): Promise<void> => reader.read().then(({ value, done }) => {
      if (done) return Promise.resolve();

      buffer += value;
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      const shouldCancel = processLines(lines);
      if (shouldCancel) return reader.cancel();

      return readNextChunk();
    });

    await readNextChunk();
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return;
    onError?.(err);
    throw err;
  }
};
